import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Cart, CartItem, Product } from '../types';

interface CartStore extends Cart {
  addItem: (product: Product, quantity?: number, selectedSize?: string, selectedColor?: string, shippingCost?: number) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  calculateTotals: () => void;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      totalItems: 0,
      totalPrice: 0,
      shippingCost: 0,
      tax: 0,
      grandTotal: 0,
      
      addItem: (product: Product, quantity = 1, selectedSize, selectedColor, shippingCost) => {
        const { items } = get();
        const existingItemIndex = items.findIndex(
          item => 
            item.product.id === product.id && 
            item.selectedSize === selectedSize && 
            item.selectedColor === selectedColor
        );
        
        if (existingItemIndex > -1) {
          // Update existing item quantity
          const updatedItems = [...items];
          updatedItems[existingItemIndex].quantity += quantity;
          if (shippingCost !== undefined) {
            const current = Number.isFinite(updatedItems[existingItemIndex].shippingCost as any)
              ? Number(updatedItems[existingItemIndex].shippingCost)
              : 0;
            const next = Number.isFinite(shippingCost as any) ? Number(shippingCost) : 0;
            // If we previously had no shipping, but now we do, persist it
            if (current <= 0 && next > 0) {
              updatedItems[existingItemIndex].shippingCost = next;
            }
          }
          set({ items: updatedItems });
        } else {
          // Add new item
          const newItem: CartItem = {
            id: `${product.id}-${selectedSize || 'default'}-${selectedColor || 'default'}`,
            product,
            quantity,
            selectedSize,
            selectedColor,
            shippingCost,
          };
          set({ items: [...items, newItem] });
        }
        
        get().calculateTotals();
      },
      
      removeItem: (itemId: string) => {
        const { items } = get();
        const updatedItems = items.filter(item => item.id !== itemId);
        set({ items: updatedItems });
        get().calculateTotals();
      },
      
      updateQuantity: (itemId: string, quantity: number) => {
        if (quantity <= 0) {
          get().removeItem(itemId);
          return;
        }
        
        const { items } = get();
        const updatedItems = items.map(item =>
          item.id === itemId ? { ...item, quantity } : item
        );
        set({ items: updatedItems });
        get().calculateTotals();
      },
      
      clearCart: () => {
        set({
          items: [],
          totalItems: 0,
          totalPrice: 0,
          shippingCost: 0,
          tax: 0,
          grandTotal: 0,
        });
      },
      
      calculateTotals: () => {
        const { items } = get();
        const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
        const totalPrice = items.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
        const shippingCost = items.reduce((sum, item) => {
          const perItem = Number.isFinite(item.shippingCost as any) ? Number(item.shippingCost) : 0;
          return sum + (perItem * item.quantity);
        }, 0);
        const tax = 0;
        const grandTotal = totalPrice + shippingCost;
        
        set({
          totalItems,
          totalPrice,
          shippingCost,
          tax,
          grandTotal,
        });
      },
    }),
    {
      name: 'cart-storage',
    }
  )
);
